import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, ActivityIndicator, Linking } from "react-native";
import { useSelector } from "react-redux";
import { MyTeamMember } from "../../types";
import { OnboardingChecklistRow } from "../types";
import { fetchOnboardingFormQuestions, completeOnboardingItem } from "../../peopleHubApi";
import FinalSignatures from "../../BeginActivity/common/components/FinalSignatures";
import { SignatureData, Signer } from "../../BeginActivity/common/types/beginActivity.common.types";
import FontFamily from "../../../../constants/fonts";
import {
  DocumentModal,
  DocumentBanner,
  DocumentDetailsCard,
  DocumentSection,
  DocumentFormGrid,
  DocumentField,
  DocumentInput,
  DocumentDateInput,
  TouchpointSource,
  DocumentResourceCard,
  DocumentNoticeCard,
  DocumentValidation,
  QuestionChecklist,
  ChecklistGroup,
  DocColors,
} from "./OnboardingDocument";

interface CompleteOnboardingModalProps {
  visible: boolean;
  onClose: () => void;
  item: OnboardingChecklistRow | null;
  member: MyTeamMember;
  managerName?: string;
  onCompleteSuccess: () => void;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

// Section comment boxes shown on a milestone review (keys are persisted in the payload).
const MILESTONE_COMMENT_FIELDS = [
  { key: "section-1", label: "Policy understanding and expectations", placeholder: "Support needed for policy understanding and expectations..." },
  { key: "section-2", label: "Current performance and feedback", placeholder: "Feedback, violations, progress, or support needs..." },
  { key: "section-3", label: "Performance metrics and targets", placeholder: "Performance metrics, target context, and attached-form notes..." },
];

const CompleteOnboardingModal: React.FC<CompleteOnboardingModalProps> = ({
  visible,
  onClose,
  item,
  member,
  managerName = "Manager",
  onCompleteSuccess,
}) => {
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const userGpid = useSelector((state: any) => state.appData?.userInfo?.GPID);
  const userEmail = useSelector((state: any) => state.appData?.userInfo?.email ?? state.auth?.user?.username);
  const [completedOn, setCompletedOn] = useState(todayIso);
  const [resourceReviewed, setResourceReviewed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [formSections, setFormSections] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [generalComment, setGeneralComment] = useState("");
  const [sectionComments, setSectionComments] = useState<Record<string, string>>({});

  // Certification fields
  const [workArea, setWorkArea] = useState(member.role || "Warehouse Associate");
  const [srlAssetId, setSrlAssetId] = useState("");
  const [anchorType, setAnchorType] = useState("Overhead rated anchorage");
  const [equipmentClass, setEquipmentClass] = useState("Electric pallet jack");
  const [knowledgeScore, setKnowledgeScore] = useState("85");
  const [practicalLocation, setPracticalLocation] = useState((member as any).location || "Auburn WA");
  const [restrictions, setRestrictions] = useState("None");

  // Signatures
  const [sigData, setSigData] = useState<SignatureData>({
    employeeSignature: undefined,
    managerSignature: undefined,
  });
  const [isSignatureUploading, setIsSignatureUploading] = useState(false);

  const itemKey = item?.itemKey || item?.id || "";
  const isMilestone = item?.type === "Milestone" || itemKey.includes("milestone");
  const isPreferredMethod = itemKey.includes("preferred-method");
  const isPitCert = itemKey.includes("pit-certification");
  const isSrlCert = itemKey.includes("srl-certification");
  const isEhs = itemKey.includes("ehs");
  const isResourceItem = !isMilestone && !isPreferredMethod && !isPitCert && !isSrlCert;

  const requiresDualSignature = isMilestone || isPreferredMethod || isEhs;

  useEffect(() => {
    if (!visible || !item) return;

    setCompletedOn(todayIso());
    setResourceReviewed(false);
    setAnswers({});
    setGeneralComment("");
    setSectionComments({});
    setSigData({ employeeSignature: undefined, managerSignature: undefined });

    if (isMilestone || isPreferredMethod) {
      // Prob_Form: 30 = Milestone 1, 60 = Milestone 2, 90 = Milestone 3. Preferred-method has no probation form.
      let probForm: "30" | "60" | "90" | undefined = "30";
      if (isPreferredMethod) probForm = undefined;
      else if (itemKey.includes("milestone2") || item?.phase === "Milestone 2") probForm = "60";
      else if (itemKey.includes("milestone3") || item?.phase === "Milestone 3") probForm = "90";

      setIsLoadingQuestions(true);
      fetchOnboardingFormQuestions({
        probForm,
        locationType: selectedLocation?.locationType,
        organizationType: selectedLocation?.organizationType,
        is3pl: selectedLocation?.is3pl,
      })
        .then((res) => {
          if (res?.sections) {
            setFormSections(res.sections);
            const initialAns: Record<string, string> = {};
            res.sections.forEach((sec: any) => {
              (sec.questions || []).forEach((q: any) => {
                initialAns[String(q.id)] = q.answerType === "ABOVE_ON_BELOW_NA" ? "Above Target/On Target" : "Yes";
              });
            });
            setAnswers(initialAns);
          }
        })
        .catch((err) => console.warn("Could not load form questions", err))
        .finally(() => setIsLoadingQuestions(false));
    } else {
      setFormSections([]);
    }
  }, [
    visible, item, itemKey, isMilestone, isPreferredMethod,
    selectedLocation?.locationType, selectedLocation?.organizationType, selectedLocation?.is3pl,
  ]);

  if (!item) return null;

  const signers: Signer[] = [
    { role: "Employee", expectedName: member.name },
    { role: "Manager", expectedName: managerName },
  ];

  const handleAnswerChange = (qId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));
  };

  const validationMessage = (): string | null => {
    if (!completedOn) return "Enter the date this item was completed.";
    if (isResourceItem && !resourceReviewed) return "Open the resource and confirm it was reviewed with the employee.";
    if (isPitCert && Number(knowledgeScore) < 80) return "A knowledge score of 80 or higher is required to certify.";
    if (!sigData.employeeSignature?.imageData) return "The employee signature is required.";
    if (requiresDualSignature && !sigData.managerSignature?.imageData) return "The manager signature is required.";
    return null;
  };

  const isFormValid = () => validationMessage() === null;

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting || isSignatureUploading) return;

    setIsSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        questionId: Number(questionId) || questionId,
        answer,
      }));

      const payload: Record<string, any> = {
        completedOn,
        completedById: userGpid,
        completedByEmail: userEmail,
        employeeName: member.name,
        completionType: item.completionType || item.type,
        resourceReviewed,
        employeeSignature: sigData.employeeSignature?.imageData,
        managerSignature: sigData.managerSignature?.imageData,
        formAnswers: formattedAnswers.length > 0 ? JSON.stringify(formattedAnswers) : null,
        comments: isMilestone && !isPreferredMethod ? JSON.stringify(sectionComments) : generalComment,
        fields: isPitCert
          ? { evaluator: managerName, equipmentClass, knowledgeScore, practicalLocation, restrictions }
          : isSrlCert
          ? { evaluator: managerName, workArea, srlAssetId, anchorType }
          : undefined,
      };

      await completeOnboardingItem(member.gpid, itemKey, payload);
      onCompleteSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error completing onboarding item:", err);
      alert(err?.response?.data?.message || err?.message || "Failed to record completion");
    } finally {
      setIsSubmitting(false);
    }
  };

  const checklistGroups: ChecklistGroup[] = formSections.map((sec, secIdx) => ({
    key: String(sec.sectionId ?? secIdx),
    title: sec.sectionLabel,
    rows: (sec.questions || []).map((q: any) => ({
      id: String(q.id),
      label: q.text,
      options:
        q.answerType === "ABOVE_ON_BELOW_NA"
          ? ["Above Target/On Target", "Below Target", "N/A"]
          : ["Yes", "No", "N/A"],
    })),
  }));

  const message = validationMessage();
  const signatureHint = requiresDualSignature
    ? "Employee and manager signatures are required."
    : "Employee signature is required.";
  const questionsTitle = isPreferredMethod ? "Touchpoint questions" : `${item.phase} review questions`;

  return (
    <DocumentModal
      visible={visible}
      title={`Complete ${item.activity}`}
      onClose={onClose}
      secondaryAction={{ text: "Cancel", onPress: onClose }}
      primaryAction={{
        text: isPitCert ? "Certify and Issue License" : "Record Completion",
        onPress: handleSubmit,
        disabled: !isFormValid() || isSubmitting || isSignatureUploading,
      }}
    >
      <DocumentBanner text="The manager launches this record and hands the device to the employee for each required signature. Finalizing creates a permanent People activity." />

      <DocumentDetailsCard
        items={[
          { label: "Employee", value: member.name },
          { label: "Document", value: item.activity },
          { label: "Manager", value: managerName },
          { label: "Due", value: item.due },
        ]}
      />

      <DocumentSection title="Record details" hint="Required fields are marked.">
        <DocumentFormGrid>
          <DocumentField label="Completed on" required>
            <DocumentDateInput value={completedOn} onChange={setCompletedOn} max={todayIso()} />
          </DocumentField>
          <DocumentField label="Completion type">
            <DocumentInput value={item.completionType || item.type} editable={false} />
          </DocumentField>
        </DocumentFormGrid>
      </DocumentSection>

      {isResourceItem && (
        <DocumentResourceCard
          title={`${item.activity} learning resource`}
          description="This placeholder will be replaced by the governed external resource URL."
          linkLabel="Open placeholder resource"
          onOpenLink={() => {
            setResourceReviewed(true);
            Linking.openURL("https://www.pepsico.com").catch(() => {});
          }}
          checkboxLabel="Resource reviewed with employee"
          checked={resourceReviewed}
          onToggle={() => setResourceReviewed((prev) => !prev)}
        />
      )}

      {isEhs && (
        <DocumentNoticeCard
          title="EHS compliance acknowledgement"
          text="The employee confirms that site safety expectations, reporting responsibilities, required personal protective equipment, and stop-work authority were reviewed. The manager confirms the conversation was completed and questions were addressed."
        />
      )}

      {isSrlCert && (
        <DocumentSection title="SRL certification details" hint="Mocked evaluator fields for review.">
          <DocumentFormGrid>
            <DocumentField label="Evaluator" required>
              <DocumentInput value={managerName} editable={false} />
            </DocumentField>
            <DocumentField label="Work area / role" required>
              <DocumentInput value={workArea} onChangeText={setWorkArea} />
            </DocumentField>
            <DocumentField label="SRL asset ID" required>
              <DocumentInput value={srlAssetId} onChangeText={setSrlAssetId} placeholder="Example: SRL-2048" />
            </DocumentField>
            <DocumentField label="Anchorage type" required>
              <DocumentInput value={anchorType} onChangeText={setAnchorType} />
            </DocumentField>
          </DocumentFormGrid>
        </DocumentSection>
      )}

      {isPitCert && (
        <DocumentSection title="PIT certification details" hint="Passing this form issues a printable 24-month PIT License.">
          <DocumentFormGrid>
            <DocumentField label="Evaluator" required>
              <DocumentInput value={managerName} editable={false} />
            </DocumentField>
            <DocumentField label="Equipment class" required>
              <DocumentInput value={equipmentClass} onChangeText={setEquipmentClass} />
            </DocumentField>
            <DocumentField label="Knowledge score" required hint="80 or higher is required.">
              <DocumentInput
                value={knowledgeScore}
                onChangeText={setKnowledgeScore}
                keyboardType="numeric"
                placeholder="0-100"
              />
            </DocumentField>
            <DocumentField label="Practical evaluation location" required>
              <DocumentInput value={practicalLocation} onChangeText={setPracticalLocation} />
            </DocumentField>
            <DocumentField label="Restrictions" required fullWidth>
              <DocumentInput value={restrictions} onChangeText={setRestrictions} />
            </DocumentField>
          </DocumentFormGrid>
        </DocumentSection>
      )}

      {isPreferredMethod && (
        <DocumentSection title="Preferred Method Checklist" hint="Existing Navigator NHO touchpoint">
          <TouchpointSource
            title="Production-aligned form state"
            text="Navigator supplies this checklist from the configured NHO Preferred Method question set. This review uses the configured questions with the same response, comment, and signature behavior."
          />
        </DocumentSection>
      )}

      {isMilestone && !isPreferredMethod && (
        <DocumentSection title={`Probationary Review Form - ${item.phase}`} hint="Existing Navigator NHO touchpoint">
          <TouchpointSource
            title="Configured NHO question set"
            text={`Production filters Key Focus Areas by location, touch type, and section. This plan carries a complete configured ${item.phase} set and locks the responses into the completed record.`}
          />
        </DocumentSection>
      )}

      {(isMilestone || isPreferredMethod) &&
        (isLoadingQuestions ? (
          <ActivityIndicator size="small" color={DocColors.blue700} style={styles.loader} />
        ) : checklistGroups.length === 0 ? (
          <DocumentSection title={questionsTitle}>
            <Text style={styles.mutedText}>No specific probation questions configured for this touchpoint.</Text>
          </DocumentSection>
        ) : (
          <QuestionChecklist
            title={questionsTitle}
            helper={
              isPreferredMethod
                ? "Preferred Method always uses Yes / No / N/A."
                : "Sections 1 and 2 use Yes / No / N/A. Performance metrics use Above Target/On Target / Below Target / N/A."
            }
            groups={checklistGroups}
            answers={answers}
            onAnswer={handleAnswerChange}
          />
        ))}

      {isPreferredMethod && (
        <DocumentSection title="Comments" hint="Navigator stores one comment for this touchpoint.">
          <DocumentField label="Additional Comments" hint="Optional">
            <DocumentInput
              multiline
              value={generalComment}
              onChangeText={setGeneralComment}
              placeholder="Additional comments about the touchpoint survey..."
            />
          </DocumentField>
        </DocumentSection>
      )}

      {isMilestone && !isPreferredMethod && (
        <DocumentSection
          title="Section comments"
          hint="Record support needs, feedback, and performance context for this touchpoint."
        >
          <View style={styles.commentsGrid}>
            {MILESTONE_COMMENT_FIELDS.map((field) => (
              <View key={field.key} style={styles.commentsCell}>
                <DocumentField label={field.label} hint="Optional">
                  <DocumentInput
                    multiline
                    value={sectionComments[field.key] ?? ""}
                    onChangeText={(text) => setSectionComments((prev) => ({ ...prev, [field.key]: text }))}
                    placeholder={field.placeholder}
                  />
                </DocumentField>
              </View>
            ))}
          </View>
        </DocumentSection>
      )}

      <DocumentSection title="Final signatures" hint={signatureHint}>
        <FinalSignatures
          bare
          signatureData={sigData}
          onSignatureChange={setSigData}
          signers={requiresDualSignature ? signers : [signers[0]]}
          activityType="routine_observation"
          onUploadingChange={setIsSignatureUploading}
        />
      </DocumentSection>

      {message && <DocumentValidation message={message} />}
    </DocumentModal>
  );
};

const styles = StyleSheet.create({
  loader: {
    padding: 20,
  },
  mutedText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textSecondary,
  },
  commentsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
  },
  commentsCell: {
    flexGrow: 1,
    flexBasis: "30%",
    minWidth: 220,
  },
});

export default CompleteOnboardingModal;
